import auth from './auth';
import { toastError } from '../../Toaster/Toast';

/**
 * @typedef {Object} RequestOptions
 * @property {boolean} [authHeader=true] - Send Authorization header with token in request
 * @property {Object} [headers={}] - Other headers to include in the request
 * @property {boolean} [rawRequest=false] - Return the original response object instead of just the payload
 */

const getAuthHeaders = () => {
	const bearerToken = auth.getBearerToken();
	let headers = {};
	if (bearerToken) headers.Authorization = `Bearer ${bearerToken}`;
	return headers;
};

const handleResponse = async (response) => {
	if (!response.ok) {
		const errorData = await response.json();
		if (response.status === 401) {

			auth.logout();
			toastError({ 
				title: 'Session Expired', 
				description: 'Your session has expired. Please log in again.' 
			});

			setTimeout(() => {
				window.location.href = '/logout?reason=session-expired';
			}, 2000);
		}
		return { ...errorData, status: response.status };
	}

	const contentType = response.headers.get('content-type') || '';
	const isBinary =
		/application\/octet-stream|application\/pdf|application\/zip|application\/vnd\.openxmlformats-officedocument\.wordprocessingml\.document|image\//i.test(
			contentType
		);

	if (isBinary) {
		const arrayBuffer = await response.arrayBuffer();
		return arrayBuffer;
	}

	if (contentType && contentType.includes('application/json')) {
		return response.json();
	}

	return response.text(); // Handle plain string response
};

const buildQueryString = (params) => {
	return Object.keys(params)
		.reduce((query, key) => {
			if (Array.isArray(params[key])) {
				return (
					query +
					params[key]
						.map((value, index) => `${key}[${index}]=${value}`)
						.join('&') +
					'&'
				);
			}
			return query + `${key}=${params[key]}&`;
		}, '?')
		.slice(0, -1);
};

const fetchRequest = async (
	method,
	endpoint,
	{
		params = {},
		data = null,
		authHeader = true,
		headers = {},
		rawRequest = false,
		...options
	} = {}
) => {
	let url = endpoint;
	if (params && Object.keys(params).length > 0) {
		url += buildQueryString(params);
	}

	const isFormData = data instanceof FormData;

	const config = {
		method,
		headers: {
			...(isFormData ? {} : { 'Content-Type': 'application/json' }),
			'x-app-id': process.env.REACT_APP_APPID,
			...headers,
			...(authHeader ? getAuthHeaders() : {}),
		},
		...options,
	};

	if (data) {
		config.body = isFormData ? data : JSON.stringify(data);
	}

	try {
		const response = await fetch(url, config);
		const responseResult = await handleResponse(response);
		return responseResult;
	} catch (error) {
		const token = auth.getBearerToken();
		if (token && (token === 'expired.invalid.token' || token.includes('invalid'))) {
			auth.logout();
			toastError({ 
				title: 'Session Expired', 
				description: 'Your session has expired. Please log in again.' 
			});

			setTimeout(() => {
				window.location.href = '/logout?reason=session-expired';
			}, 2000);
			return { errored: true, error: 'Session expired', sessionExpired: true };
		}

		return { errored: true, error: error.message, networkError: true };
	}
};

/**
 * Sends an HTTP GET request to the specified endpoint with the provided data and options.
 *
 * @param {string} endpoint - The URL or endpoint to send the GET request to.
 * @param {Object} [params={}] - The data to include in the body of the GET request.
 * @param {Object} [options={}] - Additional options to customize the request (e.g., headers).
 * @returns {Promise<any>} - A promise that resolves to the response of the fetch request.
 */
export const get = (endpoint, params = {}, options = {}) => {
	return fetchRequest('GET', endpoint, { params, ...options });
};

/**
 * Sends an HTTP POST request to the specified endpoint with the provided data and options.
 *
 * @param {string} endpoint - The URL or endpoint to send the POST request to.
 * @param {Object} [data={}] - The data to include in the body of the POST request.
 * @param {Object} [options={}] - Additional options to customize the request (e.g., headers).
 * @returns {Promise<any>} - A promise that resolves to the response of the fetch request.
 */
export const post = (endpoint, data = {}, options = {}) => {
	return fetchRequest('POST', endpoint, { data, ...options });
};

/**
 * Sends an HTTP DELETE request to the specified endpoint with the provided data and options.
 *
 * @param {string} endpoint - The URL or endpoint to send the DELETE request to.
 * @param {Object} [params={}] - The data to include in the body of the DELETE request.
 * @param {Object} [options={}] - Additional options to customize the request (e.g., headers).
 * @returns {Promise<any>} - A promise that resolves to the response of the fetch request.
 */
export const remove = (endpoint, params = {}, options = {}) => {
	return fetchRequest('DELETE', endpoint, { params, ...options });
};

/**
 * Sends an HTTP PUT request to the specified endpoint with the provided data and options.
 *
 * @param {string} endpoint - The URL or endpoint to send the PUT request to.
 * @param {Object} [data={}] - The data to include in the body of the PUT request.
 * @param {Object} [options={}] - Additional options to customize the request (e.g., headers).
 * @returns {Promise<any>} - A promise that resolves to the response of the fetch request.
 */
export const put = (endpoint, data = {}, options = {}) => {
	return fetchRequest('PUT', endpoint, { data, ...options });
};

/**
 * Sends an HTTP PATCH request to the specified endpoint with the provided data and options.
 *
 * @param {string} endpoint - The URL or endpoint to send the PATCH request to.
 * @param {Object} [data={}] - The data to include in the body of the PATCH request.
 * @param {Object} [options={}] - Additional options to customize the request (e.g., headers).
 * @returns {Promise<any>} - A promise that resolves to the response of the fetch request.
 */
export const patch = (endpoint, data = {}, options = {}) => {
	return fetchRequest('PATCH', endpoint, { data, ...options });
};

export default {
	get: (params, endpoint, options) => get(endpoint, params, options),
	post: (data, endpoint, options) => post(endpoint, data, options),
	delete: (params, endpoint) => remove(endpoint, params),
	put: (data, endpoint, options) => put(endpoint, data, options),
	patch: (data, endpoint, options) => patch(endpoint, data, options),
	putNoAuth: (data, endpoint, options) =>
		put(endpoint, data, { ...options, authHeader: false }),
};
